import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Box
} from '@mui/material';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { track } from '../../../../../utils/track';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { useNetwork } from '../../../../../utils/network';
import { useUser } from '../../../../../contexts/UserContext';
import { NewCard, PullShare } from '../../../../../types/types';
import PackRevealOverlay from './PackReveal/PackRevealOverlay';
import { RevealCard } from './PackReveal/packRevealEngine';
import {
  dialogPaperSx,
  panelSx,
  primaryBtnSx,
  ghostBtnSx
} from '../../../../../components/portalUi';
import { cardArtUrl } from '../../../NFTCollection/components/CardInspectModal';
interface Package {
  id: number;
  name: string;
  description: string;
  price: string;
  cards: string;
  tokens: number;
}

interface Pack {
  id: number;
  /* the sign-up starter pack: opening it LOCKS trading, withdrawals and ranked play until
     KYC or A$unlockAud spent (server lib/starterLock) — the row shows a disclaimer */
  starterLock?: { unlockAud: number } | null;
  name: string;
  Package: Package;
}

interface TokenClaim {
  id: number;
  tokenAmount: string;
  description: string;
  status: string;
  createdAt: string;
}

interface ReferralClaim extends TokenClaim {}

interface SpecialPackageDetails {
  packageName: string;
}

interface SpecialPackage {
  id: number;
  specialPackage: SpecialPackageDetails;
}

type ClaimItem =
  | Pack
  | ((TokenClaim | SpecialPackage) & {
      type: 'token' | 'referral' | 'special-pack';
    });
const ITEMS_PER_PAGE = 4;

// Gift-box reveal animation for the "tokens claimed" modal.
const giftShake = keyframes`
  0%, 100% { transform: rotate(0deg) translateX(0); }
  15% { transform: rotate(-8deg) translateX(-3px); }
  30% { transform: rotate(7deg) translateX(3px); }
  45% { transform: rotate(-6deg) translateX(-2px); }
  60% { transform: rotate(5deg) translateX(2px); }
  75% { transform: rotate(-3deg) translateX(-1px); }
`;
const lidFly = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(-104px) rotate(-26deg); opacity: 0; }
`;
const boxDrop = keyframes`
  0% { transform: translateY(0) scale(1); opacity: 1; }
  100% { transform: translateY(12px) scale(0.8); opacity: 0; }
`;
const coinRise = keyframes`
  0% { transform: translate(-50%, 26px) scale(0.4); opacity: 0; }
  60% { transform: translate(-50%, -8px) scale(1.14); opacity: 1; }
  100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
`;
const burstRing = keyframes`
  0% { transform: scale(0.3); opacity: 0.9; }
  100% { transform: scale(1.6); opacity: 0; }
`;
const revealPop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

// The pack-reveal engine ranks by tier 0 (common) … 4 (relic). Map the claimed
// card's rarity into the tier the animation wants.
const RARITY_TIER: Record<string, number> = {
  common: 0,
  rare: 1,
  epic: 2,
  legendary: 3,
  relic: 4
};
const toRevealCard = (c: NewCard): RevealCard => ({
  name: c.name,
  rarity: c.rarity,
  tier: RARITY_TIER[(c.rarity || '').toLowerCase()] ?? 0,
  // Same art source as the collection/inspect modal (env-aware, proven to load)
  // — a separate hardcoded CDN was why revealed cards sometimes showed no image.
  art: cardArtUrl({ type: c.type, name: c.name, rarity: c.rarity }),
  // the estimated value the claim carried (null = no active listing yet)
  valueInc: c.valueInc ?? null,
  valueUsd: c.valueUsd ?? null
});

type UnclaimedPacksWidgetProps = {
  /** Controlled "YOUR PACKS" modal, so the dashboard FAB can open it. */
  packsModalOpen?: boolean;
  onPacksModalOpenChange?: (open: boolean) => void;
  /** Reports the current unclaimed count up (for the FAB badge). */
  onCountChange?: (count: number) => void;
};

export const UnclaimedPacksWidget: React.FC<UnclaimedPacksWidgetProps> = ({
  packsModalOpen,
  onPacksModalOpenChange,
  onCountChange
}) => {
  const { get, post } = useNetwork();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [tokenClaims, setTokenClaims] = useState<TokenClaim[]>([]);
  const [referralClaims, setReferralClaims] = useState<ReferralClaim[]>([]);
  const [specialPackClaims, setSpecialPackClaims] = useState<SpecialPackage[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState<number | null>(null);
  // A failed claim used to be a silent no-op — the button just stopped spinning and
  // nothing happened, which reads as "the site is broken". Surface it instead.
  const [claimError, setClaimError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // The tokens-claimed modal opens a gift box: shake for a beat, then burst open.
  const [giftOpen, setGiftOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      setGiftOpen(false);
      return;
    }
    setGiftOpen(false);
    const t = setTimeout(() => setGiftOpen(true), 1050);
    return () => clearTimeout(t);
  }, [open]);
  const { user, setUser } = useUser();
  // Cards to play through the pack-open reveal (card packs only; null = no reveal).
  // the claimed cards and, when the server recorded the pull, its share link
  const [reveal, setReveal] = useState<{ cards: RevealCard[]; share: PullShare | null } | null>(null);
  const [claimedTokenAmount, setClaimedTokenAmount] = useState<string>('');
  const [page, setPage] = useState(1);
  // The unclaimed list now lives behind a button, in a "YOUR PACKS" modal.
  // Controllable so the dashboard FAB's "Unpack packs" item can open it.
  const [showPacksInternal, setShowPacksInternal] = useState(false);
  const showPacks = packsModalOpen ?? showPacksInternal;
  const setShowPacks = (v: boolean) =>
    (onPacksModalOpenChange ?? setShowPacksInternal)(v);
  const navigate = useNavigate();

  // Combine packs and token claims for display
  const allClaims: ClaimItem[] = [
    ...packs,
    ...tokenClaims.map((claim) => ({ ...claim, type: 'token' as const })),
    ...referralClaims.map((claim) => ({ ...claim, type: 'referral' as const })),
    ...specialPackClaims.map((claim) => ({
      ...claim,
      type: 'special-pack' as const
    }))
  ];

  // Surface the count so the dashboard FAB can badge "packs ready to open".
  useEffect(() => {
    onCountChange?.(allClaims.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClaims.length]);

  const totalPages = Math.ceil(allClaims.length / ITEMS_PER_PAGE);
  const displayedClaims = allClaims.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchUnclaimedItems = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both packs and token claims
      const [
        packsResult,
        tokenClaimsResult,
        referralResult,
        specialPackResult
      ] = await Promise.all([
        get('/fulfilment/check', true),
        get('/token-claim/pending', true),
        get('/referral-commission/pending', true),
        get('/special-pack-transactions/pending', true)
      ]);
      setPacks(packsResult);
      setTokenClaims(tokenClaimsResult);
      setReferralClaims(referralResult);
      setSpecialPackClaims(specialPackResult);
    } catch (error) {
      console.error('Error fetching unclaimed items:', error);
    }
    setLoading(false);
  }, []);

  // Initial load + a light refresh whenever a purchase drops a new pack. This
  // replaces the old post-purchase window.location.reload() (which was wiping
  // the pack-open reveal); the PurchaseModal now fires 'incrypt:packs-updated'.
  useEffect(() => {
    fetchUnclaimedItems();
    const onPacksUpdated = () => fetchUnclaimedItems();
    window.addEventListener('incrypt:packs-updated', onPacksUpdated);
    return () =>
      window.removeEventListener('incrypt:packs-updated', onPacksUpdated);
  }, [fetchUnclaimedItems]);

  const claimItem = async (item: ClaimItem) => {
    setClaiming(item.id);
    setClaimError(null);
    try {
      if ('type' in item && item.type === 'token') {
        // Handle token claim
        const result = await post(
          '/token-claim/claim',
          { claimId: item.id },
          true
        );
        setClaimedTokenAmount(result.claimedAmount);
        setUser({
          ...user,
          earnedTokens: result.earnedTokens
        });
        setTokenClaims((prev) => prev.filter((claim) => claim.id !== item.id));
        setOpen(true);
      } else if ('type' in item && item.type === 'referral') {
        const result = await post(
          '/referral-commission/claim',
          { claimId: item.id },
          true
        );

        setClaimedTokenAmount(
          (parseFloat(result.claimedAmount) * 100000).toFixed(5)
        );
        setUser({
          ...user,
          earnedTokens: result.earnedTokens
        });
        setReferralClaims((prev) =>
          prev.filter((claim) => claim.id !== item.id)
        );
        setOpen(true);
      } else if ('type' in item && item.type === 'special-pack') {
        const result = await post(
          '/special-pack-transactions/claim',
          { specialPackId: item.id },
          true
        );
        track('pack.claim', {
          kind: 'special',
          packId: item.id,
          cards: ((result && result.selectedCards) || []).length
        });

        {
          const claimedCards: NewCard[] = result.selectedCards || [];
          // Card pack → the pull reveal is the whole experience (no summary dialog).
          if (claimedCards.length > 0) setReveal({ cards: claimedCards.map(toRevealCard), share: result.share || null });
        }
        setUser({
          ...user,
          earnedTokens: result.earnedTokens
        });
        setPacks((prev) => prev.filter((pack) => pack.id !== item.id));
      } else {
        // Handle pack claim
        const result = await post(
          '/fulfilment/complete-order',
          { fulfilmentId: item.id },
          true
        );
        track('pack.claim', {
          kind: 'pack',
          packId: item.id,
          cards: ((result && result.selectedCards) || []).length
        });
        {
          const claimedCards: NewCard[] = result.selectedCards || [];
          // Card pack → the pull reveal is the whole experience (no summary dialog).
          if (claimedCards.length > 0) setReveal({ cards: claimedCards.map(toRevealCard), share: result.share || null });
        }
        setUser({
          ...user,
          earnedTokens: result.earnedTokens
        });
        setPacks((prev) => prev.filter((pack) => pack.id !== item.id));
      }
    } catch (error) {
      console.error('Error claiming item:', error);
      // Reconcile with the server so the list reflects what was actually consumed
      // (the claim may have committed server-side even though this response failed),
      // and tell the user rather than leaving them staring at an unchanged count.
      setClaimError('That didn’t go through. Nothing was lost — please try again.');
      fetchUnclaimedItems();
    }
    setClaiming(null);
  };

  const formatTokenBalance = (tokenAmount: string): string => {
    if (!tokenAmount || tokenAmount === '0') {
      return '0.00000';
    }

    const numericAmount = parseFloat(tokenAmount);
    if (isNaN(numericAmount)) {
      return '0.00000';
    }

    return (numericAmount / 100000).toFixed(5);
  };

  const getItemDisplayName = (item: ClaimItem): string => {
    if (
      ('type' in item && item.type === 'token') ||
      ('type' in item && item.type === 'referral') ||
      ('type' in item && item.type === 'special-pack')
    ) {
      if ('description' in item) {
        return item.description;
      }
      return item.specialPackage.packageName;
    }
    return (item as Pack).Package.name;
  };

  const getItemDisplayInfo = (item: ClaimItem): string => {
    if (
      ('type' in item && item.type === 'token') ||
      ('type' in item && item.type === 'referral') ||
      ('type' in item && item.type === 'special-pack')
    ) {
      if ('tokenAmount' in item) {
        return `${formatTokenBalance(item.tokenAmount)} INC Tokens`;
      }
    }
    return '';
  };

  return (
    <>
      <div className="dash-panel accent-cyan">
        <div className="dash-phead">
          <Inventory2Icon className="ic" /> Package Unwrap
        </div>
        <div className="dash-pbody dash-center">
          {loading ? (
            <CircularProgress sx={{ color: '#38e8ff' }} />
          ) : allClaims.length > 0 ? (
            <>
              <div className="dash-big cy">{allClaims.length}</div>
              <div className="dash-unit">
                {allClaims.length === 1 ? 'pack' : 'packs'} ready to open
              </div>
              <div className="dash-prow" style={{ justifyContent: 'center' }}>
                <button
                  type="button"
                  className="dash-btn solid alert"
                  data-track="dashboard.open_packs"
                  onClick={() => {
                    setPage(1);
                    setShowPacks(true);
                  }}
                >
                  Open packs
                </button>
              </div>
            </>
          ) : (
            <>
              <p
                className="dash-note"
                style={{ textAlign: 'center', margin: 0 }}
              >
                No packs to open right now.
              </p>
              <div className="dash-prow" style={{ justifyContent: 'center' }}>
                <button
                  type="button"
                  className="dash-btn"
                  data-track="dashboard.buy_more_packs"
                  onClick={() => navigate('giveaway')}
                >
                  Buy more packs
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog
        open={showPacks}
        onClose={() => setShowPacks(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <Typography
            sx={{
              font: '900 20px ui-monospace, Menlo, Consolas, monospace',
              letterSpacing: '0.18em',
              color: '#38e8ff'
            }}
          >
            YOUR PACKS
          </Typography>
          {allClaims.length > 0 && (
            <Typography
              sx={{
                mt: 0.75,
                font: '700 10px ui-monospace, Menlo, Consolas, monospace',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: '#8ea3b3'
              }}
            >
              {allClaims.length} ready to open
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {claimError && (
            <Typography
              sx={{ textAlign: 'center', color: '#ff8a9b', fontSize: 13, mb: 1.5 }}
            >
              {claimError}
            </Typography>
          )}
          {allClaims.length > 0 ? (
            <>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}
              >
                {displayedClaims.map((item: ClaimItem) => {
                  const info = getItemDisplayInfo(item);
                  const isToken = !!info;
                  // The row wraps where it is narrow: beside the 96px button a
                  // 320px phone left the name and amount ~20px, so a claim read
                  // "R…" / "5…". There the button now goes under them.
                  return (
                    <Box
                      key={`${'type' in item ? item.type : 'pack'}-${item.id}`}
                      sx={{
                        ...panelSx,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          minWidth: 0,
                          flex: '1 1 160px'
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            borderRadius: '10px',
                            display: 'grid',
                            placeItems: 'center',
                            background: isToken
                              ? 'rgba(255,207,64,0.12)'
                              : 'rgba(56,232,255,0.12)',
                            color: isToken ? '#ffcf40' : '#38e8ff'
                          }}
                        >
                          {isToken ? (
                            <MonetizationOnIcon sx={{ fontSize: 21 }} />
                          ) : (
                            <Inventory2Icon sx={{ fontSize: 21 }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{ fontWeight: 800, fontSize: 14, color: '#e8f4fb' }}
                          >
                            {getItemDisplayName(item)}
                          </Typography>
                          <Typography
                            noWrap
                            sx={
                              isToken
                                ? {
                                    font: '800 12px ui-monospace, Menlo, Consolas, monospace',
                                    color: '#38e8ff'
                                  }
                                : {
                                    font: '700 10px ui-monospace, Menlo, Consolas, monospace',
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: '#8ea3b3'
                                  }
                            }
                          >
                            {info || 'Card pack'}
                          </Typography>
                          {'starterLock' in item && item.starterLock && (
                            <Typography sx={{ mt: 0.5, fontSize: 11.5, lineHeight: 1.35, color: '#ffd36b' }}>
                              Heads up: opening this pack locks its cards from trading and withdrawal,
                              and closes ranked play on your account, until you verify your identity or
                              spend A${item.starterLock.unlockAud} on INCRYPT. Casual play stays open.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Button
                        onClick={() => claimItem(item)}
                        disabled={!!claiming}
                        sx={{ ...primaryBtnSx, minWidth: 96, flexShrink: 0, ml: 'auto' }}
                      >
                        {claiming === item.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : isToken ? (
                          'Claim'
                        ) : (
                          'Open'
                        )}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#8ea3b3',
                        fontWeight: 700
                      },
                      '& .MuiPaginationItem-root.Mui-selected': {
                        background: 'rgba(56,232,255,0.18)',
                        color: '#38e8ff'
                      }
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ textAlign: 'center', py: 3, color: '#8ea3b3' }}>
              No unclaimed items available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setShowPacks(false)}
            sx={{ ...ghostBtnSx, minWidth: 120 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          if (reason === 'escapeKeyDown') return;
          setOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, px: 3 }}>
          {/* Gift box: shakes for a beat, then bursts open — lid flies off, the
              body drops away and a coin rises out — before the amount pops in. */}
          <Box
            sx={{
              position: 'relative',
              height: 168,
              mb: 0.5,
              display: 'grid',
              placeItems: 'center'
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                width: 190,
                height: 190,
                borderRadius: '50%',
                pointerEvents: 'none',
                background:
                  'radial-gradient(circle, rgba(56,232,255,0.5), rgba(255,207,64,0.14) 45%, transparent 70%)',
                opacity: 0,
                ...(giftOpen && { animation: `${burstRing} 0.8s ease-out 0.1s` })
              }}
            />

            <Box
              sx={{
                position: 'relative',
                width: 108,
                height: 100,
                animation: giftOpen
                  ? 'none'
                  : `${giftShake} 0.6s ease-in-out 0.15s 3`
              }}
            >
              {/* lid + bow — flies off */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 18,
                  left: -6,
                  right: -6,
                  height: 26,
                  zIndex: 2,
                  ...(giftOpen && {
                    animation: `${lidFly} 0.55s cubic-bezier(.3,.1,.3,1) forwards`
                  })
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '7px',
                    background: 'linear-gradient(180deg,#5cf2ff,#2ec7e6)',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.45)'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 'calc(50% - 8px)',
                    width: 16,
                    background: '#ffcf40'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -15,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex'
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 20,
                      border: '5px solid #ffcf40',
                      borderRight: 0,
                      borderRadius: '60% 0 0 60%',
                      mr: '-3px'
                    }}
                  />
                  <Box
                    sx={{
                      width: 18,
                      height: 20,
                      border: '5px solid #ffcf40',
                      borderLeft: 0,
                      borderRadius: '0 60% 60% 0'
                    }}
                  />
                </Box>
              </Box>

              {/* body */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 74,
                  borderRadius: '8px',
                  zIndex: 1,
                  background: 'linear-gradient(180deg,#38e8ff,#159dbb)',
                  boxShadow:
                    '0 12px 26px -8px rgba(56,232,255,0.6), inset 0 2px 0 rgba(255,255,255,0.3)',
                  ...(giftOpen && {
                    animation: `${boxDrop} 0.5s ease-in 0.18s forwards`
                  })
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 'calc(50% - 8px)',
                    width: 16,
                    background: '#ffcf40'
                  }}
                />
              </Box>

              {/* coin rising out */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 24,
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  zIndex: 0,
                  transform: 'translate(-50%, 0)',
                  background: 'rgba(56,232,255,0.16)',
                  border: '1px solid rgba(56,232,255,0.55)',
                  boxShadow: '0 0 28px -4px rgba(56,232,255,0.8)',
                  opacity: 0,
                  ...(giftOpen && {
                    animation: `${coinRise} 0.6s cubic-bezier(.2,.9,.3,1.3) 0.2s forwards`
                  })
                }}
              >
                <MonetizationOnIcon sx={{ fontSize: 30, color: '#38e8ff' }} />
              </Box>
            </Box>
          </Box>

          {/* amount — pops in once the box is open */}
          <Box
            sx={{
              opacity: 0,
              ...(giftOpen && {
                animation: `${revealPop} 0.5s ease-out 0.35s both`
              })
            }}
          >
            <Typography
              sx={{
                font: '800 10px ui-monospace, Menlo, Consolas, monospace',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#8ea3b3'
              }}
            >
              Tokens claimed
            </Typography>
            <Typography
              sx={{
                mt: 1,
                font: '900 42px ui-monospace, Menlo, Consolas, monospace',
                lineHeight: 1,
                color: '#38e8ff',
                textShadow: '0 0 32px rgba(56,232,255,0.35)'
              }}
            >
              {formatTokenBalance(claimedTokenAmount)}{' '}
              <Box component="span" sx={{ fontSize: 20, color: '#8ea3b3' }}>
                INC
              </Box>
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#8ea3b3', fontSize: 14 }}>
              Added to your balance.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{ ...primaryBtnSx, minWidth: 140 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      {reveal && (
        <PackRevealOverlay
          cards={reveal.cards}
          share={reveal.share}
          onClose={() => {
            setReveal(null);
            // Reconcile the count with the server once the reveal is done, so the
            // badge can never drift from what was actually consumed — belt-and-
            // suspenders over the optimistic removal, and it self-corrects if the
            // tab was reloaded mid-flow.
            fetchUnclaimedItems();
          }}
        />
      )}
    </>
  );
};
